import type { CSSProperties, LinariaClassName } from '@linaria/core';
import { cx } from '@linaria/core';
import { MouseEventHandler, Ref, useRef } from 'react';
import { avatarUrl } from '../../mfcLogic/avatarPreviewUrl';
import { APPSTATE } from '../../state';
import { setPinHelperID } from '../../state/internal/rooms';
import { isTouchDevice } from '../../utilities/browser';
import { hideDragHelper, highlightDragHelper, showDragHelper } from '../Hooks/useDragHelper';
import { useUserSelector } from '../Hooks/useUser';
import Dot from './Dot';
import DragHandle from './DragHandle';
import "./BadgeAvatar.linaria.module.css!=!./BadgeAvatar?./BadgeAvatar.linaria.module.css?css=H4sIAAAAAAAAA5WT0W6cMBBF3/MV+1IpleoI0002cT7GMvYYBoxNbWNgrfx7xW6XLJHy0DeuOXMHz1yeROz5b81pEr6fMFfOK/CMDvMhOIPqkIR/JKT2sJBey5/vHw9Pa8lby6PpoDBZjj44zwaHNoK/vU8Nt52rdMoJfEQpDBEGa8t6VMrAZhM4pS9LIbqsMAxGLAytQQtEG5hvVN/xpuz79iUPLmBEZ5kHIyKmzaiqOKpB18e9yqsNCY1H27HiBpeKz16edbNXuRKyq70brSLSGefZ9fIiiSg80cKYlSB32DqXbSZlzZNMUDd7dW+LvajhG9vRm80KSn4qT69U5gawbuK+5Hq2wWnhtPDFSeo8oYrNHr4cbayWnDYvrh3PX2RuxxBRL0Q6G8FGJuF+n+XEqdL+OLRf5P3tAp6BSZc+y5pdt03lSxYIRujDl0bPLachVKiaf1kkXigcA3suftyYLnD6nP5Mk89rM23cxBpUCuw2korXZpl0n8cAngQwICOzzu6SF+cJk9mSdx+5UvG3YaYa9ur/I7JN/lRwerLljOtPtmZSKDd9V74BW3n0nM5xaUHn6IUN2vmeXZ6MiPBY/DoUn60Mp2352s5lvn7khRuEBxtvTDtsG3RVCzISjXG/vO7Iu+pk0zm7QUiMC6PvHw9/AcmyQlwyBAAA"

/** display pixel ratio */
const DPR = 2;
const DEFAULT_AVATAR_SIZE = 40;
const stylesBorder = "atm_3f_1varmwi";
const stylesBadgePointer = "atm_9j_tlke0l";
const stylesBadgeContainer = "atm_vh_nkobfv atm_9s_116y0ak atm_mk_h2mmj6 atm_bb_idpfg4";
const stylesAvatarContainer = "atm_2d_xrczfh atm_2g_vcvegh atm_e2_72781c atm_vy_10r07cf atm_fc_1h6ojuz atm_2w_1dfr4pj atm_mk_h2mmj6 atm_h_1h6ojuz atm_5j_1ssbidh atm_ks_15vqwwr atm_vb_glywfm atm_9s_1txwivl";
const stylesBoxShadow = "atm_2d_9px1fe atm_70_17n2xii";
const stylesAvatarImage = "atm_e2_72781c atm_vy_10r07cf atm_tr_1xtyjef atm_7l_1j28jx2 atm_5j_1ssbidh atm_jp_1dfr4pj atm_k4_kb7nvz";
type BadgeAvatarProps = {
  avatarImageClassName?: LinariaClassName;
  dragHandleClassName?: LinariaClassName;
  avatarClassName?: LinariaClassName;
  className?: LinariaClassName;
  isBroadcasting?: boolean;
  isOffline?: boolean;
  disableBadge?: boolean;
  disableDrag?: boolean;
  addBoxShadow?: boolean;
  ref?: Ref<HTMLElement>;
  border?: boolean;
  avatar?: string;
  small?: boolean;
  title?: string;
  size?: number;
  alt?: string;
  id?: number;
  onMouseEnter?: MouseEventHandler<HTMLSpanElement>;
  onMouseLeave?: MouseEventHandler<HTMLSpanElement>;
  onClick?: MouseEventHandler<HTMLSpanElement>;
};
const BadgeAvatar = (props: BadgeAvatarProps) => {
  const {
    size = DEFAULT_AVATAR_SIZE,
    avatarImageClassName,
    dragHandleClassName,
    avatarClassName,
    className,
    isBroadcasting,
    isOffline,
    disableBadge,
    disableDrag,
    addBoxShadow,
    avatar,
    border,
    small,
    title,
    ref,
    alt,
    id,
    onMouseEnter,
    onMouseLeave,
    onClick
  } = props;
  const refDragMoveThrottle = useRef<number>(null);
  const refDragHandle = useRef<HTMLSpanElement>(null);
  const refImage = useRef<HTMLImageElement>(null);
  const isPublicShow = useUserSelector(id, 'isPublicShow');
  const enableDrag = !isTouchDevice && isBroadcasting && isPublicShow && !disableDrag;
  const avatarSize = small || size * DPR < 50 ? 50 : size * DPR < 100 ? 100 : size * DPR < 150 ? 150 : 300;
  const haloWidth = size < 24 ? 1.5 : 2;
  const dotSize = size < 24 ? 6 : 8;
  const avatarSrc = avatar ? avatar : avatarUrl(id, avatarSize);
  const dragStart = event => {
    if (isTouchDevice) return;
    if (!enableDrag) {
      setPinHelperID(0);
      return;
    }
    // console.log('[BadgeAvatar] dragStart', id)
    // 1) set up the drag image so we've got a consistent look
    event.dataTransfer.setData('text/plain', id);
    event.dataTransfer.setDragImage(refImage.current, 22, 22);
    event.dataTransfer.effectAllowed = 'move';
    // 2) open/show the drag receiver area
    showDragHelper(id);
  };
  const dragMove = event => {
    if (isTouchDevice) return;
    const {
      clientX,
      clientY
    } = event;
    if (refDragMoveThrottle.current) {
      cancelAnimationFrame(refDragMoveThrottle.current);
    }
    refDragMoveThrottle.current = requestAnimationFrame(() => {
      highlightDragHelper(clientX, clientY);
    });
  };
  const dragEnd = () => {
    if (isTouchDevice) return;
    if (refDragMoveThrottle.current) {
      cancelAnimationFrame(refDragMoveThrottle.current);
      refDragMoveThrottle.current = null;
    }
    hideDragHelper();
  };
  const mouseEnter = event => {
    if (refDragHandle.current && enableDrag) {
      if (APPSTATE.app.isMobile || isTouchDevice) return;
      refDragHandle.current.style.opacity = '1';
      // showDragHelper(id)
    }
    onMouseEnter?.(event);
  };
  const mouseLeave = event => {
    if (refDragHandle.current) {
      refDragHandle.current.style.opacity = '0';
      hideDragHelper();
    }
    onMouseLeave?.(event);
  };
  return <span data-testid="Container__BadgeAvatar" onMouseEnter={mouseEnter} onMouseLeave={mouseLeave} id={`avatar-${id}`} onClick={onClick} title={title} ref={ref} className={cx(!!onClick && stylesBadgePointer, stylesBadgeContainer, className)}>
            <div data-testid={`Badge__UserBadge_${id || alt}`} style={{
      '--avatar-height': `${size}px`,
      '--avatar-width': `${size}px`
    } as CSSProperties} className={cx(stylesAvatarContainer, addBoxShadow && stylesBoxShadow, avatarClassName)}>
                <img data-testid={isOffline ? `BadgeAvatar__Offline` : `BadgeAvatar__Online`} crossOrigin="anonymous" src={avatarSrc} ref={refImage} alt={alt} className={cx(stylesAvatarImage, border && stylesBorder, avatarImageClassName)} style={{
        height: size || '100%',
        width: size || '100%'
      }} />
            </div>
            {!disableDrag && <DragHandle onDragStart={dragStart} onDragEnd={dragEnd} onDrag={dragMove} className={cx(dragHandleClassName)} enableDrag={enableDrag} ref={refDragHandle} size={size} />}
            <Dot isBroadcasting={isBroadcasting} disableBadge={disableBadge} isOffline={isOffline} haloWidth={haloWidth} dotSize={dotSize} />
        </span>;
};
export default BadgeAvatar;